import {
  HookFormInput,
  HookQuerySearchSelect,
} from 'ui-component/HookFormComponents';
import { packageValueOptions, StockLotQuerySchema } from 'types/inventory';
import { createKitRequestLineFormConstants } from 'views/kitting/Kit/components/constants';
import { useGetStockLotsServerSideQuery } from 'store/slices/apiV1/inventory';
import _ from 'lodash';
import { useFormContext, useWatch } from 'react-hook-form';
import { useKitRequestContext } from 'views/kitting/Kit/KitRequestContext';
import { useLazyGetAllocationsClientV2Query } from 'store/slices/clientV2/allocations';
import { ALL, QueryParams } from 'types/api';
import { DatagridFilterOperatorsValues } from 'types/datagrid';
import { useCallback, useEffect } from 'react';
import { calculateAvailableQuantForStockLot } from 'views/kitting/Kit/utils';
import { DecoratedKitRequestLine } from 'hooks/useGetDecoratedKitRequestLines';
import { CustomProperty } from 'types/customProperty';
import { Stack } from '@mui/system';
import { HookFormCustomPropertyInput } from 'ui-component/HookFormComponents/HookFormCustomPropertyInput';
import { useFeature } from '@growthbook/growthbook-react';
import { FeatureFlags } from 'types';
import {
  UnitOfMeasureDetails,
  UnitOfMeasureOptions,
} from 'types/unitsOfMeasure';

interface BaseCreateUpdateKitRequestFromStockLotProps {
  showStockLotSelect?: boolean;
  calculateAvailableQuantWithKitRequestLine?: boolean;
  customProperties?: CustomProperty[];
}

type CreateUpdateKitRequestFromStockLotProps =
  BaseCreateUpdateKitRequestFromStockLotProps &
    (
      | {
          isCreate: true;
          kitRequestLine?: never;
        }
      | {
          isCreate: false;
          kitRequestLine?: DecoratedKitRequestLine;
          showStockLotSelect?: boolean;
          calculateAvailableQuantWithKitRequestLine?: boolean;
        }
    );

const CreateUpdateKitRequestFromStockLot = ({
  isCreate = false,
  kitRequestLine,
  showStockLotSelect = false,
  calculateAvailableQuantWithKitRequestLine = true,
  customProperties,
}: CreateUpdateKitRequestFromStockLotProps) => {
  const tempUoM = useFeature(FeatureFlags.tempUoM).on;
  const { kitRequest } = useKitRequestContext();

  const [getAllocations] = useLazyGetAllocationsClientV2Query();

  const {
    control,
    formState: { errors },
    setValue,
    setError,
    clearErrors,
    trigger,
  } = useFormContext();

  const watchStockLot = useWatch({
    control,
    name: createKitRequestLineFormConstants.stockLot.id,
  });

  const handleGetAllocationsForStockLot = async (stockLotId: string) => {
    const { data: allocations } = await getAllocations({
      schema: [
        ALL,
        {
          stockLot: [],
        },
      ],
      filters: [
        {
          field: 'stockLot.id',
          operator: DatagridFilterOperatorsValues.isAnyOf,
          value: stockLotId,
        },
      ],
    }).unwrap();
    const available = calculateAvailableQuantForStockLot({
      allocations,
      stockLotQuant: watchStockLot?.quant,
      kitRequest,
      kitRequestLineId: calculateAvailableQuantWithKitRequestLine
        ? kitRequestLine?.id!
        : undefined,
    });
    if (available < watchStockLot.quant) {
      setError(
        tempUoM
          ? createKitRequestLineFormConstants.targetDimensionValueStockLot.id
          : createKitRequestLineFormConstants.targetQuantityStockLot.id,
        {
          message: `${
            available === 0
              ? 'All quantity of this stock lot has already been allocated. Please select another stock lot.'
              : `This stock lot only has an available quantity of ${available}. Please select another stock lot${
                  isCreate
                    ? ' or create kit line for the part'
                    : calculateAvailableQuantWithKitRequestLine
                    ? ''
                    : ' or allocate only the available quantity'
                }.`
          }`,
        }
      );
    } else {
      trigger(
        tempUoM
          ? createKitRequestLineFormConstants.targetDimensionValueStockLot.id
          : createKitRequestLineFormConstants.targetQuantityStockLot.id
      );
    }
  };

  useEffect(() => {
    if (watchStockLot?.id && watchStockLot?.quant) {
      clearErrors([
        createKitRequestLineFormConstants.targetQuantityStockLot.id,
        createKitRequestLineFormConstants.targetDimensionValueStockLot.id,
      ]);
      handleGetAllocationsForStockLot(watchStockLot.id);
    }
  }, [watchStockLot?.id]);

  const overrideOnChangeStockLot = useCallback(
    (value: StockLotQuerySchema | StockLotQuerySchema[]) => {
      if (value instanceof Array) return value;
      const stockLot = value;
      if (isCreate) {
        setValue(
          createKitRequestLineFormConstants.targetQuantityStockLot.id,
          stockLot?.quant
        );
        setValue(
          createKitRequestLineFormConstants.targetDimensionValueStockLot.id,
          stockLot?.dimensionValue
        );
        setValue(
          createKitRequestLineFormConstants.dimensionClassStockLot.id,
          stockLot?.dimensionClass ?? null,
          {
            shouldValidate: true,
          }
        );
        setValue(
          createKitRequestLineFormConstants.dimensionUnitStockLot.id,
          stockLot?.dimensionUnit ?? null,
          {
            shouldValidate: true,
          }
        );
      }

      return stockLot;
    },
    [isCreate, setValue]
  );

  const queryFilters = [
    {
      field: 'quant',
      operator: '>',
      value: 0,
    },
    'and',
    {
      field: 'facility.id',
      operator: 'isAnyOf',
      value: _.map(kitRequest?.sourceLocations, 'id'),
    },
    'or',
    {
      field: 'stockLocation.id',
      operator: 'isAnyOf',
      value: _.map(kitRequest?.sourceLocations, 'id'),
    },
  ];

  const watchDimensionUnit = useWatch({
    control,
    name: createKitRequestLineFormConstants.dimensionUnitStockLot.id,
  });
  const handleChangeStockLot = useCallback(
    (value: StockLotQuerySchema | StockLotQuerySchema[]) => {
      if (value instanceof Array) return;
      const stockLot = value;
      setValue(
        createKitRequestLineFormConstants.dimensionClassStockLot.id,
        stockLot?.dimensionClass ?? null
      );
      setValue(
        createKitRequestLineFormConstants.dimensionUnitStockLot.id,
        stockLot?.dimensionUnit ?? null
      );
      setValue(createKitRequestLineFormConstants.stockLot.id, stockLot);
    },
    [setValue]
  );
  return (
    <>
      {Boolean(isCreate || kitRequestLine?.locked || showStockLotSelect) && (
        <HookQuerySearchSelect<StockLotQuerySchema>
          errors={errors}
          control={control}
          name={createKitRequestLineFormConstants.stockLot.id}
          label={createKitRequestLineFormConstants.stockLot.label}
          overrideOnChange={overrideOnChangeStockLot}
          handleChange={handleChangeStockLot}
          getDefaultValue={(
            option:
              | Pick<
                  StockLotQuerySchema,
                  | 'quant'
                  | 'id'
                  | 'part'
                  | 'dimensionClass'
                  | 'dimensionUnit'
                  | 'dimensionValue'
                >
              | Pick<StockLotQuerySchema, 'id'>
          ) => option?.id}
          useGetQuery={useGetStockLotsServerSideQuery}
          filters={
            (!isCreate && kitRequestLine?.v2OrgPart
              ? [
                  {
                    field: 'v2OrgPart',
                    operator: DatagridFilterOperatorsValues.isAnyOf,
                    value: [
                      kitRequestLine?.v2OrgPart,
                      ...(kitRequestLine?.altV2OrgParts ?? []),
                    ],
                  },
                  ...queryFilters,
                ]
              : queryFilters) as QueryParams<StockLotQuerySchema>['filters']
          }
          schema={[
            'quant',
            'lotId',
            'package',
            'dimensionClass',
            'dimensionUnit',
            'dimensionValue',
            'partLotId',
            {
              v2OrgPart: ['mpn', 'cofactrId', 'id'],
            },
          ]}
          getOptionLabel={(option) => {
            if (_.every(Object.values(option), _.isNil)) {
              return '';
            }
            const abbreviation =
              UnitOfMeasureDetails[option.dimensionUnit]?.abbreviation;
            const formattedQuantity = tempUoM
              ? `${option.dimensionValue}${abbreviation}`
              : `${option.quant}pc`;
            return `${option.v2OrgPart?.mpn}: ${formattedQuantity} ${
              option.package
                ? ` -  ${
                    _.find(packageValueOptions, ['value', option.package])
                      ?.label
                  }`
                : ''
            } (${option.v2OrgPart?.cofactrId}${option.lotId})`;
          }}
        />
      )}
      {tempUoM ? (
        <Stack direction="row" spacing={2}>
          <HookFormInput
            control={control}
            name={
              createKitRequestLineFormConstants.targetDimensionValueStockLot.id
            }
            label={
              createKitRequestLineFormConstants.targetDimensionValueStockLot
                .label
            }
            InputProps={{
              endAdornment: watchDimensionUnit
                ? UnitOfMeasureDetails[
                    watchDimensionUnit as UnitOfMeasureOptions
                  ]?.abbreviation
                : undefined,
            }}
            type="number"
            errors={errors}
            sx={{ flex: 1 }}
            boxSx={{ flex: 1 }}
          />
        </Stack>
      ) : (
        <HookFormInput
          control={control}
          name={createKitRequestLineFormConstants.targetQuantityStockLot.id}
          label={createKitRequestLineFormConstants.targetQuantityStockLot.label}
          type="number"
          errors={errors}
        />
      )}

      {customProperties && (
        <Stack sx={{ mt: 2 }}>
          {_.map(customProperties, (property) => (
            <HookFormCustomPropertyInput
              control={control}
              errors={errors}
              key={property.key}
              customProperty={property}
              setValue={setValue}
            />
          ))}
        </Stack>
      )}
    </>
  );
};

export default CreateUpdateKitRequestFromStockLot;
